import type { StoreApi, UseBoundStore } from "zustand";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { ulid } from "@core/foundation/id";
import type { LoggerContract } from "@core/foundation/logger";
import { isNullish } from "@core/foundation/types";
import { z } from "@core/zod";

import type { StoreSelectors } from "./store.ts";
import {
  createStoreProxy,
  createStoreSelectors,
  resetOnMigration,
} from "./store.ts";

const SessionStateSchema = z.object(
  { redeemedCodes: z.array(z.string()), session: z.safeId().nullable() },
  {
    redeemedCodes: "The codes the returns already redeemed, newest first",
    session: "The nonce of the redirect awaiting its return, or null when none",
  },
);

export interface SessionState extends z.infer<typeof SessionStateSchema> {}

interface SessionActions {
  readonly endSession: () => void;
  readonly isCodeRedeemed: (code: string) => boolean;
  readonly redeemCode: (code: string) => void;
  readonly startSession: () => string;
}

export interface SessionStore extends SessionActions, SessionState {}

export interface SessionStoreConfig {
  readonly logger: LoggerContract;
  readonly storeKey: string;
  readonly storeVersion: number;
}

interface SessionStoreResult {
  readonly sessionSelectors: StoreSelectors<SessionState>;
  readonly sessionStore: SessionStore;
  readonly useSessionStore: UseBoundStore<StoreApi<SessionStore>>;
}

export function createSessionStore(
  config: SessionStoreConfig,
): SessionStoreResult {
  const initialState: SessionState = { redeemedCodes: [], session: null };
  const useSessionStore = create<SessionStore>()(
    persist(
      (set, get) => {
        return {
          ...initialState,
          endSession: () => {
            set({ session: null });
          },
          isCodeRedeemed: code => {
            const { redeemedCodes } = get();

            return redeemedCodes.includes(code);
          },
          redeemCode: code => {
            set(state => {
              const MAX_CODES = 100;

              return {
                redeemedCodes: [code, ...state.redeemedCodes].slice(
                  0,
                  MAX_CODES,
                ),
              };
            });
          },
          startSession: () => {
            const session = ulid();

            set({ session });

            return session;
          },
        };
      },
      {
        merge: (persistedState, currentState) => {
          if (isNullish(persistedState)) {
            return currentState;
          }

          const { data, error } = SessionStateSchema.safeParse(persistedState);

          if (error) {
            config.logger.error("Failed to merge state", error);

            return currentState;
          }

          return { ...currentState, ...data };
        },
        migrate: resetOnMigration,
        name: config.storeKey,
        partialize: state => {
          const { redeemedCodes, session } = state;

          return { redeemedCodes, session } satisfies SessionState;
        },
        version: config.storeVersion,
      },
    ),
  );

  const sessionStore = createStoreProxy(useSessionStore.getState);

  const sessionSelectors = createStoreSelectors<SessionState>();

  return { sessionSelectors, sessionStore, useSessionStore };
}
